import { FC, Suspense, lazy } from 'react';
import { Route, Routes } from 'react-router-dom';

import { LoadingLayout } from '../components/LoadingLayout';
import { useCheckoutSessionParamMessages } from '../hooks/useCheckoutSessionParamMessages';
import { CompanyType } from '../models';
import Layout from '../views/Layout/Layout';
import SignInGuard from './SignInGuard';

const NoMatchView = lazy(function lazyLoad() {
  return import('../views/Layout/NoMatch');
});

const AssignmentDiscussionAttemptsView = lazy(
  () => import('../views/School/CourseSections/Assignments/Discussions/DiscussionAttemptsView')
);

const AssignmentQuestionsView = lazy(function lazyLoad() {
  return import('../views/School/CourseSections/Questions/AssignmentQuestionsView');
});

const AssignmentPromptsView = lazy(function lazyLoad() {
  return import('../views/School/CourseSections/Questions/AssignmentPromptsView');
});

const LtiLaunchView = lazy(function lazyLoad() {
  return import('../views/Lti/LaunchView');
});

const ConnectionRequestView = lazy(function lazyLoad() {
  return import('../views/ConnectionRequest/ConnectionRequestView');
});

const CourseLaunchView = lazy(function lazyLoad() {
  return import('../views/Course/LaunchView');
});

const AssignmentAttemptView = lazy(function lazyLoad() {
  return import('../views/School/AssignmentAttempt/AssignmentAttemptView');
});

const SchoolQuestionView = lazy(function lazyLoad() {
  return import('../views/School/CourseSections/Questions/QuestionView');
});

const SchoolCreateQuestionView = lazy(function lazyLoad() {
  return import('../views/School/CourseSections/Questions/CreateQuestionView');
});

const SchoolProductView = lazy(function lazyLoad() {
  return import('../views/School/Products/ProductView');
});

const SchoolAIQuestionView = lazy(function lazyLoad() {
  return import('../views/School/CourseSections/Questions/AIQuestionView');
});

const CreatePublisherInvoiceView = lazy(function lazyLoad() {
  return import('../views/Publisher/Invoice/CreateInvoiceView');
});

const CreatePublisherPaymentView = lazy(function lazyLoad() {
  return import('../views/Publisher/Payment/CreatePaymentView');
});

const PublisherInvoiceView = lazy(function lazyLoad() {
  return import('../views/Publisher/Invoice/InvoiceView');
});

const PublisherInvoicesView = lazy(function lazyLoad() {
  return import('../views/Publisher/Invoice/InvoicesView');
});

const PublisherTransactionView = lazy(function lazyLoad() {
  return import('../views/Publisher/Transaction/TransactionView');
});

const PublisherTransactionsView = lazy(function lazyLoad() {
  return import('../views/Publisher/Transaction/TransactionsView');
});

const PublisherEnrollmentView = lazy(function lazyLoad() {
  return import('../views/Publisher/Enrollment/EnrollmentView');
});

const AddInstructorContentView = lazy(function lazyLoad() {
  return import('../views/School/CourseSections/Contents/AddContentView');
});

const QuestionReportCard = lazy(function lazyLoad() {
  return import('../views/School/CourseSections/Questions/QuestionReportCard');
});

const StudentAssignmentReportCard = lazy(function lazyLoad() {
  return import('../views/School/CourseSections/Students/StudentAssignmentReportCard');
});

const StudentCourseSectionReportCard = lazy(function lazyLoad() {
  return import('../views/School/CourseSections/Students/StudentCourseSectionReportCard');
});

const CreatePublisherAssignmentView = lazy(function lazyLoad() {
  return import('../views/Publisher/Assignment/CreateAssignmentView');
});

const SchoolAssignmentView = lazy(function lazyLoad() {
  return import('../views/School/CourseSections/Assignments/AssignmentView');
});

const NewSchoolAssignmentView = lazy(function lazyLoad() {
  return import('../views/School/CourseSections/Assignments/NewAssignmentView');
});

const PublisherAssignmentView = lazy(function lazyLoad() {
  return import('../views/Publisher/Assignment/AssignmentView');
});

const CreatePublisherEnrollmentView = lazy(function lazyLoad() {
  return import('../views/Publisher/Enrollment/CreateEnrollmentView');
});

const PublisherCoursewareView = lazy(function lazyLoad() {
  return import('../views/Publisher/ContentSelection');
});

const PublisherBuildCoursewareView = lazy(function lazyLoad() {
  return import('../views/Publisher/ContentSelection');
});

const PublisherPriceCoursewareView = lazy(function lazyLoad() {
  return import('../views/Publisher/ContentSelection');
});

const PublisherAdoptCoursewareView = lazy(function lazyLoad() {
  return import('../views/Publisher/ContentSelection');
});

const PublisherLaunchCoursewareView = lazy(function lazyLoad() {
  return import('../views/Publisher/ContentSelection');
});

const AdministratorMathsView = lazy(function lazyLoad() {
  return import('../views/Edition/Catalog/Maths');
});

const PublisherManageCoursewareView = lazy(function lazyLoad() {
  return import('../views/Publisher/ContentSelection');
});

const ModuleView = lazy(function lazyLoad() {
  return import('../views/School/CourseSections/Modules/ModuleView');
});

const PublisherBookView = lazy(function lazyLoad() {
  return import('../views/Publisher/Book/BookView');
});

const CreatePublisherEditionView = lazy(function lazyLoad() {
  return import('../views/Publisher/Edition/CreateEditionView');
});

const PublisherEditionView = lazy(function lazyLoad() {
  return import('../views/Publisher/Edition/EditionView');
});

const BillingPeriodsView = lazy(function lazyLoad() {
  return import('../views/BillingPeriods/BillingPeriodsView');
});

const BillingListView = lazy(function lazyLoad() {
  return import('../views/BillingPeriods/BillingList');
});

const BillingPeriodView = lazy(function lazyLoad() {
  return import('../views/BillingPeriods/BillingPeriod');
});

const BillingChargesView = lazy(function lazyLoad() {
  return import('../views/BillingPeriods/ChargesView');
});

const AccountingStatementsView = lazy(function lazyLoad() {
  return import('../views/BillingPeriods/AccountingStatementsView');
});

const BillingTransactionView = lazy(function lazyLoad() {
  return import('../views/BillingPeriods/TransactionView');
});

const PublisherCloneEditionView = lazy(function lazyLoad() {
  return import('../views/Publisher/Editions/CloneEditionView');
});

const CreatePublisherProductView = lazy(function lazyLoad() {
  return import('../views/Publisher/Product/CreateProductView');
});

const CreatePublisherCoursewareView = lazy(function lazyLoad() {
  return import('../views/Publisher/Courseware/CreateCoursewareView');
});

const AttendanceView = lazy(function lazyLoad() {
  return import('../views/School/Attendance/AttendanceView');
});

const NewModuleView = lazy(function lazyLoad() {
  return import('../views/School/CourseSections/Settings/Module/NewModuleView');
});

const AttendanceQuestionView = lazy(function lazyLoad() {
  return import('../views/School/Attendance/QuestionView');
});

const PollView = lazy(function lazyLoad() {
  return import('../views/School/Polling/PollView');
});

const PollQuestionView = lazy(function lazyLoad() {
  return import('../views/School/Polling/QuestionView');
});

const PublisherLaunchView = lazy(function lazyLoad() {
  return import('../views/Publisher/Launch/LaunchView');
});

const CreatePublisherQuestionView = lazy(function lazyLoad() {
  return import('../views/Publisher/Question/CreateQuestionView');
});

const UploadPublisherQuestionsView = lazy(function lazyLoad() {
  return import('../views/Publisher/Question/UploadQuestionsView');
});

const PublisherQuestionView = lazy(function lazyLoad() {
  return import('../views/Publisher/Question/QuestionView');
});

const CreatePublisherLaunchView = lazy(function lazyLoad() {
  return import('../views/Publisher/Launch/CreateLaunchView');
});

const PublisherAdoptionView = lazy(function lazyLoad() {
  return import('../views/Publisher/Adoption/AdoptionView');
});

const ClonePublisherAdoptionView = lazy(function lazyLoad() {
  return import('../views/Publisher/Adoption/CloneAdoptionView');
});

const ProductDemosView = lazy(function lazyLoad() {
  return import('../views/Administrator/ProductDemosView');
});

const CreateAdoptionView = lazy(function lazyLoad() {
  return import('../views/Assignment/Adoption/CreateAdoptionView');
});

const CreatePublisherAdoptionView = lazy(function lazyLoad() {
  return import('../views/Publisher/Adoption/CreateAdoptionView');
});

const CreateBulkAccessCodeView = lazy(function lazyLoad() {
  return import('../views/AccessCode/CreateBulkAccessCodeView');
});

const PublisherProductView = lazy(function lazyLoad() {
  return import('../views/Publisher/Product/ProductView');
});

const PublisherTermView = lazy(function lazyLoad() {
  return import('../views/Publisher/Term/TermView');
});

const AssignmentGroupsPairView = lazy(function lazyLoad() {
  return import('../views/School/CourseSections/Settings/AssignmentGroup/AssignmentGroupPairs');
});

const ModulesPairView = lazy(function lazyLoad() {
  return import('../views/School/CourseSections/Settings/Module/ModulePairs');
});

const AssignmentsPairView = lazy(function lazyLoad() {
  return import('../views/School/CourseSections/Settings/Assignment/AssignmentPairs');
});

const PublisherPriceView = lazy(function lazyLoad() {
  return import('../views/Publisher/Price/PriceView');
});

const CreatePublisherBookView = lazy(function lazyLoad() {
  return import('../views/Publisher/Book/CreateBookView');
});

const CreatePublisherPriceView = lazy(function lazyLoad() {
  return import('../views/Publisher/Price/CreatePriceView');
});

const AssignmentView = lazy(function lazyLoad() {
  return import('../views/Assignment/AssignmentView');
});

const ConnectionsView = lazy(function lazyLoad() {
  return import('../views/Connection/ConnectionView');
});

const Kitchensink = lazy(function lazyLoad() {
  return import('../views/Kitchensink/Kitchensink');
});

const AssignmentGroupView = lazy(function lazyLoad() {
  return import('../views/AssignmentGroup/AssignmentGroupView');
});

const PriceAssignView = lazy(function lazyLoad() {
  return import('../views/Price/PriceAssignView');
});

const TaskView = lazy(function lazyLoad() {
  return import('../views/Administrator/Tasks/TaskView');
});

const MondayView = lazy(function lazyLoad() {
  return import('../views/Administrator/Tasks/MondayView');
});

const JobView = lazy(function lazyLoad() {
  return import('../views/Job/JobView');
});

const TermView = lazy(function lazyLoad() {
  return import('../views/Term/TermView');
});

const CreateContentView = lazy(function lazyLoad() {
  return import('../views/Content/CreateContentView');
});

const PublisherCatalogView = lazy(function lazyLoad() {
  return import('../views/Publisher/Catalog/CatalogView');
});

const CatalogEditionView = lazy(function lazyLoad() {
  return import('../views/Edition/Catalog/EditionView');
});

const CatalogLearningObjectivesView = lazy(function lazyLoad() {
  return import('../views/Edition/Catalog/LearningObjectives');
});

const ApiTestView = lazy(function lazyLoad() {
  return import('../views/ApiTest/ApiTest');
});

const CatalogImageAccessibilityJobsView = lazy(function lazyLoad() {
  return import('../views/Edition/Catalog/ImageAccessibilityJobsView');
});

const CatalogImageAccessibilityJobView = lazy(function lazyLoad() {
  return import('../views/Edition/Catalog/ImageAccessibilityJobView');
});

const CatalogEditionConversionJobsView = lazy(function lazyLoad() {
  return import('../views/Edition/Catalog/ConversionJobsView');
});

const CatalogEditionConversionJobView = lazy(function lazyLoad() {
  return import('../views/Edition/Catalog/ConversionJobView');
});

const CatalogEditionAssessmentJobsView = lazy(function lazyLoad() {
  return import('../views/Edition/Catalog/AssessmentJobsView');
});

const CatalogEditionAssessmentJobView = lazy(function lazyLoad() {
  return import('../views/Edition/Catalog/AssessmentJobView');
});

const OnixJobsView = lazy(function lazyLoad() {
  return import('../views/Onix/OnixJobsView');
});

const OnixJobView = lazy(function lazyLoad() {
  return import('../views/Onix/OnixJobView');
});

const CatalogEditionTableOfContentsView = lazy(function lazyLoad() {
  return import('../views/Edition/Catalog/TableOfContentsView');
});

const PublisherTitleRequestsView = lazy(function lazyLoad() {
  return import('../views/Publisher/TitleRequestsView');
});

const PublisherDemosView = lazy(function lazyLoad() {
  return import('../views/Administrator/Publisher/PublisherDemosView');
});

const PublisherAdoptionsView = lazy(function lazyLoad() {
  return import('../views/Publisher/AdoptionRequestsView');
});

const CatalogEditionSupplementalsView = lazy(function lazyLoad() {
  return import('../views/Edition/Catalog/SupplementalsView');
});

const CatalogEditionSlidesView = lazy(function lazyLoad() {
  return import('../views/Edition/Catalog/Slides/SlidesView');
});

const CatalogEditionGenerateFlashcardsView = lazy(function lazyLoad() {
  return import('../views/Edition/Catalog/Flashcards/AIGenerateFlashcards');
});

const CatalogEditionGenerateSlidesView = lazy(function lazyLoad() {
  return import('../views/Edition/Catalog/Slides/AIGenerateSlidesView');
});

const CatalogEditionUploadSlidesView = lazy(function lazyLoad() {
  return import('../views/Edition/Catalog/Slides/UploadSlidesView');
});

const CatalogEditionQuestionsView = lazy(function lazyLoad() {
  return import('../views/Edition/Catalog/QuestionsView');
});

const CatalogEditionPromptsView = lazy(function lazyLoad() {
  return import('../views/Edition/Catalog/PromptsView');
});

const CatalogEditionFlashcardsView = lazy(function lazyLoad() {
  return import('../views/Edition/Catalog/FlashcardsView');
});

const CatalogEditionEditQuestionView = lazy(function lazyLoad() {
  return import('../views/Edition/Catalog/Questions/EditQuestionView');
});

const SchoolEditFlashcardView = lazy(function lazyLoad() {
  return import('../views/School/CourseSections/Flashcards/EditFlashcardView');
});

const CatalogEditionEditFlashcardView = lazy(function lazyLoad() {
  return import('../views/Edition/Catalog/Flashcards/EditFlashcardView');
});

const CatalogEditionNewFlashcardView = lazy(function lazyLoad() {
  return import('../views/Edition/Catalog/Flashcards/NewFlashcardView');
});

const CatalogEditionNewQuestionView = lazy(function lazyLoad() {
  return import('../views/Edition/Catalog/Questions/NewQuestionView');
});

const CatalogEditionGenerateQuestionsView = lazy(function lazyLoad() {
  return import('../views/Edition/Catalog/Questions/AIGenerateQuestions');
});

const CatalogNewEditionView = lazy(function lazyLoad() {
  return import('../views/Edition/Catalog/NewEditionView');
});

const CatalogProvisionRequestView = lazy(function lazyLoad() {
  return import('../views/Administrator/CourseRequests/ProvisionRequestView');
});

const CoursesProvisionCourseSectionView = lazy(function lazyLoad() {
  return import('../views/CourseSection/ConfirmProvisionCourseSectionView');
});

const CloneJobView = lazy(function lazyLoad() {
  return import('../views/CourseSection/CloneCourseSectionJobView');
});

const CoursesCloneCourseSectionView = lazy(function lazyLoad() {
  return import('../views/CourseSection/ConfirmCloneCourseSectionView');
});

const AdministratorAdoptionsView = lazy(function lazyLoad() {
  return import('../views/Administrator/Adoptions/AdoptionsView');
});

const AdministratorCreateAdoptionView = lazy(function lazyLoad() {
  return import('../views/Administrator/Adoptions/CreateAdoptionView');
});

const PublisherCreateAdoptionView = lazy(function lazyLoad() {
  return import('../views/Publisher/Adoption/CreateAdoptionView');
});

const AdministratorCatalogView = lazy(function lazyLoad() {
  return import('../views/Administrator/Catalog/CatalogView');
});

const AdministratorCreateSchoolView = lazy(function lazyLoad() {
  return import('../views/Administrator/School/AdministratorCreateSchoolView');
});

const AdministratorCreateCoursewareView = lazy(function lazyLoad() {
  return import('../views/Administrator/Courseware/CreateCoursewareView');
});

const AdministratorCreatePriceView = lazy(function lazyLoad() {
  return import('../views/Administrator/Price/AdministratorCreatePriceView');
});

const CatalogCreateCoursewareView = lazy(function lazyLoad() {
  return import('../views/Edition/Catalog/CreateCoursewareView');
});

const AdministratorApproveCatalogRequestView = lazy(function lazyLoad() {
  return import('../views/Administrator/Catalog/ApproveCatalogRequestView');
});

const AIGenerateFlashcardsView = lazy(function lazyLoad() {
  return import('../views/School/CourseSections/Flashcards/AIGenerateFlashcards');
});

const AIGenerateQuestionsView = lazy(function lazyLoad() {
  return import('../views/School/CourseSections/Questions/AIGenerateQuestions');
});

const AdministratorNewCatalogRequestView = lazy(function lazyLoad() {
  return import('../views/Administrator/Catalog/NewCatalogRequestView');
});

const NewCatalogRequestView = lazy(function lazyLoad() {
  return import('../views/Publisher/Catalog/NewCatalogRequestView');
});

const AdministratorNewCourseView = lazy(function lazyLoad() {
  return import('../views/Administrator/CourseRequests/NewCourseView');
});

const AdministratorTermsView = lazy(function lazyLoad() {
  return import('../views/Administrator/School/Terms/TermsView');
});

const AdministratorCompaniesView = lazy(function lazyLoad() {
  return import('../views/Administrator/Company/CompaniesView');
});
const SchoolsListView = lazy(function lazyLoad() {
  return import('../views/Administrator/School/SchoolsListView');
});

const DistributorsListView = lazy(function lazyLoad() {
  return import('../views/Administrator/Distributor/DistributorsListView');
});

const PublishersListView = lazy(function lazyLoad() {
  return import('../views/Administrator/Publisher/PublishersListView');
});

const AdministratorAdoptionEmailsView = lazy(function lazyLoad() {
  return import('../views/Administrator/Adoptions/AdoptionEmailsView');
});

const PersonSchoolAssignmentsView = lazy(function lazyLoad() {
  return import('../views/Person/SchoolAssignmentsView');
});

const AdministratorDistributorView = lazy(function lazyLoad() {
  return import('../views/Administrator/Distributor/DistributorView');
});

const CourseOnboardingView = lazy(function lazyLoad() {
  return import('../views/School/V2/Instructor/Onboarding/CourseConfigurationView');
});

const CourseSyllabusOnboardingView = lazy(function lazyLoad() {
  return import('../views/School/V2/Instructor/Onboarding/CourseSyllabusOnboardingView');
});

const AdministratorPublisherView = lazy(function lazyLoad() {
  return import('../views/Administrator/Publisher/PublisherView');
});

const AdministratorPublisherFeesView = lazy(function lazyLoad() {
  return import('../views/Administrator/Publisher/Fees/PublisherFeesView');
});

const AdministratorSchoolView = lazy(function lazyLoad() {
  return import('../views/Administrator/School/SchoolView');
});

const AddDistributorView = lazy(function lazyLoad() {
  return import('../views/Administrator/Distributor/AddDistributorView');
});

const AddPublisherView = lazy(function lazyLoad() {
  return import('../views/Administrator/Publisher/AddPublisherView');
});

const AdministratorSchoolTermView = lazy(function lazyLoad() {
  return import('../views/Administrator/School/Terms/TermView');
});

const AdministratorConnectionLtiJobSchedulesView = lazy(function lazyLoad() {
  return import('../views/Administrator/School/Connections/LtiJobs/LtiJobSchedulesView');
});

const AdministratorConnectionApiJobsView = lazy(function lazyLoad() {
  return import('../views/Administrator/School/Connections/ApiJobs/ApiJobsView');
});

const AdministratorConnectionApiJobSchedulesView = lazy(function lazyLoad() {
  return import('../views/Administrator/School/Connections/ApiJobs/ApiJobSchedulesView');
});

const AdministratorConnectionLtiJobView = lazy(function lazyLoad() {
  return import('../views/Administrator/School/Connections/LtiJobs/LtiJobView');
});
const AdministratorConnectionLtiJobsView = lazy(function lazyLoad() {
  return import('../views/Administrator/School/Connections/LtiJobs/LtiJobsView');
});

const AdministratorConnectionView = lazy(function lazyLoad() {
  return import('../views/Administrator/School/Connections/ConnectionView');
});

const AdministratorConnectionsView = lazy(function lazyLoad() {
  return import('../views/Administrator/School/Connections/ConnectionsView');
});

const AdministratorCoursesView = lazy(function lazyLoad() {
  return import('../views/Administrator/CourseRequests/CoursesView');
});

const AdministratorCourseRequestView = lazy(function lazyLoad() {
  return import('../views/Administrator/CourseRequests/CourseRequest');
});

const PublisherCourseRequestView = lazy(function lazyLoad() {
  return import('../views/Publisher/CourseRequests/CourseRequest');
});

const AdministratorDashboardsView = lazy(function lazyLoad() {
  return import('../views/Administrator/Dashboards/BillingDashboard');
});

const AdministratorIdentifiersView = lazy(function lazyLoad() {
  return import('../views/Administrator/Identifiers/IdentifiersView');
});

const PublisherCoursesView = lazy(function lazyLoad() {
  return import('../views/Publisher/CourseRequests/CoursesView');
});

const PublisherNewCourseRequestView = lazy(function lazyLoad() {
  return import('../views/Publisher/CourseRequests/NewCourseRequestView');
});

const PublisherCourseView = lazy(function lazyLoad() {
  return import('../views/Publisher/CourseSection/Courses/CourseView');
});

const NoteContentView = lazy(function lazyLoad() {
  return import('../views/Content/NoteContentView');
});

const ContentView = lazy(function lazyLoad() {
  return import('../views/Content/ContentView');
});

const DocumentContentView = lazy(function lazyLoad() {
  return import('../views/BookOutline/ContentContentView');
});

const CreatePublisherContentView = lazy(function lazyLoad() {
  return import('../views/Publisher/Content/CreateContentView');
});

const VideoQueueView = lazy(function lazyLoad() {
  return import('../views/Administrator/VideoQueue/VideoQueueView');
});

const PublisherContentView = lazy(function lazyLoad() {
  return import('../views/Publisher/Content/ContentView');
});

const CreateJobScheduleView = lazy(function lazyLoad() {
  return import('../views/JobSchedule/CreateJobScheduleView');
});

const LtiJobScheduleView = lazy(function lazyLoad() {
  return import('../views/JobSchedule/LtiJobScheduleView');
});

const JobScheduleView = lazy(function lazyLoad() {
  return import('../views/JobSchedule/JobScheduleView');
});

const CreateProductView = lazy(function lazyLoad() {
  return import('../views/Product/CreateProductView');
});

const PricesView = lazy(function lazyLoad() {
  return import('../views/Price/PricesView');
});

const ProductView = lazy(function lazyLoad() {
  return import('../views/Product/ProductView');
});

const UploadSchoolSlidesView = lazy(function lazyLoad() {
  return import('../views/School/CourseSections/Slides/UploadSlidesView');
});

const GenerateSchoolSlidesView = lazy(function lazyLoad() {
  return import('../views/School/CourseSections/Slides/AIGenerateSlidesView');
});

const NewSchoolQuestionView = lazy(function lazyLoad() {
  return import('../views/School/CourseSections/Questions/NewQuestionView');
});

const CreateConnectionView = lazy(function lazyLoad() {
  return import('../views/Connection/CreateConnectionView');
});

const CreateJobView = lazy(function lazyLoad() {
  return import('../views/Job/CreateJobView');
});

const ContentProductView = lazy(function lazyLoad() {
  return import('../views/BookOutline/ContentProductView');
});

const ContentEditionView = lazy(function lazyLoad() {
  return import('../views/BookOutline/ContentEditionView');
});

const CreateCostView = lazy(function lazyLoad() {
  return import('../views/Cost/CreateCostView');
});

const CreatePersonView = lazy(function lazyLoad() {
  return import('../views/Person/CreatePersonView');
});

const CreateQuestionView = lazy(function lazyLoad() {
  return import('../views/Question/CreateQuestionView');
});

const CreatePriceView = lazy(function lazyLoad() {
  return import('../views/Price/CreatePriceView');
});

const CreateEnrollmentView = lazy(function lazyLoad() {
  return import('../views/Enrollment/CreateEnrollmentView');
});

const CreateCourseSectionView = lazy(function lazyLoad() {
  return import('../views/CourseSection/CreateCourseSectionView');
});

const PublisherCourseSectionView = lazy(function lazyLoad() {
  return import('../views/Publisher/CourseSection/CourseSectionView');
});

const CreateTermView = lazy(function lazyLoad() {
  return import('../views/Term/CreateTermView');
});

const CreateCompanyView = lazy(function lazyLoad() {
  return import('../views/Company/CreateCompanyView');
});

const CreateBookView = lazy(function lazyLoad() {
  return import('../views/Book/CreateBookView');
});

const BookView = lazy(function lazyLoad() {
  return import('../views/Book/BookView');
});

const EditionView = lazy(function lazyLoad() {
  return import('../views/Edition/EditionView');
});

const CreateEditionView = lazy(function lazyLoad() {
  return import('../views/Edition/CreateEditionView');
});

const StudentsAssignmentView = lazy(function lazyLoad() {
  return import('../views/School/CourseSections/Assignments/Students/StudentsAssignmentView');
});

const QuestionView = lazy(function lazyLoad() {
  return import('../views/Question/QuestionView');
});

const EnrollmentAssignmentsView = lazy(function lazyLoad() {
  return import('../views/School/CourseSections/Students/EnrollmentAssignments');
});

const CostView = lazy(function lazyLoad() {
  return import('../views/Cost/CostView');
});

const PriceView = lazy(function lazyLoad() {
  return import('../views/Price/PriceView');
});

const TransactionView = lazy(function lazyLoad() {
  return import('../views/Transaction/TransactionView');
});

const PersonView = lazy(function lazyLoad() {
  return import('../views/Person/PersonView');
});

const EnrollmentView = lazy(function lazyLoad() {
  return import('../views/Enrollment');
});

const TermsOfUseView = lazy(function lazyLoad() {
  return import('../views/Legal/TermsOfUse');
});

const PrivacyPolicyView = lazy(function lazyLoad() {
  return import('../views/Legal/PrivacyPolicy');
});

const PeopleView = lazy(function lazyLoad() {
  return import('../views/Person/PeopleView');
});

const AccountingCallback = lazy(function lazyLoad() {
  return import('../views/Accounting/OauthCallback');
});

const CanvasOAuthCallback = lazy(function lazyLoad() {
  return import('../views/OAuth/Canvas/OauthCallback');
});

const OAuthCallback = lazy(function lazyLoad() {
  return import('../views/OAuth/OauthCallback');
});

const CourseSectionView = lazy(function lazyLoad() {
  return import('../views/CourseSection/CourseSectionView');
});

const CompanyProfileView = lazy(function lazyLoad() {
  return import('../views/Company/CompanyProfileView');
});

const PersonProfileView = lazy(function lazyLoad() {
  return import('../views/Person/PersonProfileView');
});

const CompanyView = lazy(function lazyLoad() {
  return import('../views/Company/CompanyView');
});

const CompaniesView = lazy(function lazyLoad() {
  return import('../views/Company/CompaniesView');
});

const SignUpView = lazy(function lazyLoad() {
  return import('../views/SignUp/SignUp');
});

const SignInView = lazy(function lazyLoad() {
  return import('../views/SignIn/SignIn');
});

const StatusView = lazy(function lazyLoad() {
  return import('../views/Status/StatusView');
});

const MessagesView = lazy(function lazyLoad() {
  return import('../views/Messages/MessagesView');
});

const GcloudView = lazy(function lazyLoad() {
  return import('../views/Gcloud/GcloudView');
});

const GcloudCallback = lazy(function lazyLoad() {
  return import('../views/Gcloud/OauthCallback');
});

const CanvasView = lazy(function lazyLoad() {
  return import('../views/OAuth/Canvas/CanvasView');
});

const AccountingView = lazy(function lazyLoad() {
  return import('../views/Accounting/AccountingView');
});

const PublisherDemoView = lazy(function lazyLoad() {
  return import('../views/Publisher/PublisherDemoView');
});

const AccessCodesView = lazy(function lazyLoad() {
  return import('../views/AccessCode/AccessCodesView');
});

const MainView = lazy(function lazyLoad() {
  return import('../views/Main/Main');
});

const AcademicMain = lazy(() => import('../views/School/V2/AcademicMain'));

const RouteList: FC = () => {
  useCheckoutSessionParamMessages();

  return (
    <Routes>
      {/* Unprotected routes */}
      <Route>
        <Route
          path="/status"
          element={
            <Suspense fallback={<LoadingLayout showAfterDelay />}>
              <StatusView />
            </Suspense>
          }
        />
        <Route path="/kitchensink" element={<Kitchensink />} />
        <Route
          path="/signIn"
          element={
            <Suspense fallback={<LoadingLayout showAfterDelay />}>
              <SignInView />
            </Suspense>
          }
        />
        <Route
          path="/demo/:demoName"
          element={
            <Suspense fallback={<LoadingLayout showAfterDelay />}>
              <PublisherDemoView />
            </Suspense>
          }
        />
        <Route
          path="/canvas/callback"
          element={
            <Suspense fallback={<LoadingLayout showAfterDelay />}>
              <CanvasOAuthCallback />
            </Suspense>
          }
        />
        <Route
          path="/quickbooks/callback"
          element={
            <Suspense fallback={<LoadingLayout showAfterDelay />}>
              <AccountingCallback />
            </Suspense>
          }
        />
        <Route
          path="/oauth/callback"
          element={
            <Suspense fallback={<LoadingLayout showAfterDelay />}>
              <OAuthCallback />
            </Suspense>
          }
        />
        <Route
          path="/courseLaunch/:code"
          element={
            <Suspense fallback={<LoadingLayout showAfterDelay />}>
              <CourseLaunchView />
            </Suspense>
          }
        />
        <Route
          path="/ltiLaunch"
          element={
            <Suspense fallback={<LoadingLayout showAfterDelay />}>
              <LtiLaunchView />
            </Suspense>
          }
        />
        <Route
          path="/signup"
          element={
            <Suspense fallback={<LoadingLayout showAfterDelay />}>
              <SignUpView />
            </Suspense>
          }
        />
      </Route>

      <Route element={<SignInGuard />}>
        {/* Course section routes */}
        <Route>
          <Route
            path="/school/courseSections/:courseSectionId?"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <AcademicMain />
              </Suspense>
            }
          >
            <Route
              path="/school/courseSections/:courseSectionId/content/new"
              element={
                <Suspense fallback={<LoadingLayout showAfterDelay />}>
                  <AddInstructorContentView />
                </Suspense>
              }
            />
            <Route
              path="/school/courseSections/:courseSectionId/assignments/:assignmentId/questions/:questionId"
              element={
                <Suspense fallback={<LoadingLayout showAfterDelay />}>
                  <QuestionReportCard />
                </Suspense>
              }
            />
            <Route
              path="/school/courseSections/:courseSectionId/course/onboard"
              element={
                <Suspense fallback={<LoadingLayout showAfterDelay />}>
                  <CourseOnboardingView />
                </Suspense>
              }
            />
            <Route
              path="/school/courseSections/:courseSectionId/syllabus/onboard"
              element={
                <Suspense fallback={<LoadingLayout showAfterDelay />}>
                  <CourseSyllabusOnboardingView />
                </Suspense>
              }
            />
            <Route
              path="/school/courseSections/:courseSectionId/questions/:questionId"
              element={
                <Suspense fallback={<LoadingLayout showAfterDelay />}>
                  <QuestionReportCard />
                </Suspense>
              }
            />
            <Route
              path="/school/courseSections/:courseSectionId/flashcards/generate"
              element={
                <Suspense fallback={<LoadingLayout showAfterDelay />}>
                  <AIGenerateFlashcardsView />
                </Suspense>
              }
            />
            <Route
              path="/school/courseSections/:courseSectionId/questions/generate"
              element={
                <Suspense fallback={<LoadingLayout showAfterDelay />}>
                  <AIGenerateQuestionsView />
                </Suspense>
              }
            />
            <Route
              path="/school/courseSections/:courseSectionId/assignments/:assignmentId/students/:studentId"
              element={
                <Suspense fallback={<LoadingLayout showAfterDelay />}>
                  <StudentAssignmentReportCard />
                </Suspense>
              }
            />
            <Route
              path="/school/courseSections/:courseSectionId/students/:studentId"
              element={
                <Suspense fallback={<LoadingLayout showAfterDelay />}>
                  <StudentCourseSectionReportCard />
                </Suspense>
              }
            />

            <Route
              path="/school/courseSections/:courseSectionId/assignments/:assignmentId/responses/:attemptId?"
              element={
                <Suspense fallback={<LoadingLayout showAfterDelay />}>
                  <AssignmentDiscussionAttemptsView />
                </Suspense>
              }
            />
            <Route
              path="/school/courseSections/:courseSectionId/assignments/:assignmentId/students"
              element={
                <Suspense fallback={<LoadingLayout showAfterDelay />}>
                  <StudentsAssignmentView />
                </Suspense>
              }
            />
            <Route
              path="/school/courseSections/:courseSectionId/slides/generate"
              element={
                <Suspense fallback={<LoadingLayout showAfterDelay />}>
                  <GenerateSchoolSlidesView />
                </Suspense>
              }
            />
            <Route
              path="/school/courseSections/:courseSectionId/:type/:id/questions/new"
              element={
                <Suspense fallback={<LoadingLayout showAfterDelay />}>
                  <NewSchoolQuestionView />
                </Suspense>
              }
            />
            <Route
              path="/school/courseSections/:courseSectionId/modules/new"
              element={
                <Suspense fallback={<LoadingLayout showAfterDelay />}>
                  <NewModuleView />
                </Suspense>
              }
            />
            <Route
              path="/school/courseSections/:courseSectionId/flashcards/:flashcardId"
              element={
                <Suspense fallback={<LoadingLayout showAfterDelay />}>
                  <SchoolEditFlashcardView />
                </Suspense>
              }
            />
            <Route
              path="/school/courseSections/:courseSectionId/slides/upload"
              element={
                <Suspense fallback={<LoadingLayout showAfterDelay />}>
                  <UploadSchoolSlidesView />
                </Suspense>
              }
            />
            <Route
              path="/school/courseSections/:courseSectionId/assignments/new"
              element={
                <Suspense fallback={<LoadingLayout showAfterDelay />}>
                  <NewSchoolAssignmentView />
                </Suspense>
              }
            />
            <Route
              path="/school/courseSections/:courseSectionId/assignmentGroups/pair"
              element={
                <Suspense fallback={<LoadingLayout showAfterDelay />}>
                  <AssignmentGroupsPairView />
                </Suspense>
              }
            />
            <Route
              path="/school/courseSections/:courseSectionId/modules/pair"
              element={
                <Suspense fallback={<LoadingLayout showAfterDelay />}>
                  <ModulesPairView />
                </Suspense>
              }
            />
            <Route
              path="/school/courseSections/:courseSectionId/enrollment/:enrollmentId/assignments"
              element={
                <Suspense fallback={<LoadingLayout showAfterDelay />}>
                  <EnrollmentAssignmentsView />
                </Suspense>
              }
            />
            <Route
              path="/school/courseSections/:courseSectionId/assignments/pair"
              element={
                <Suspense fallback={<LoadingLayout showAfterDelay />}>
                  <AssignmentsPairView />
                </Suspense>
              }
            />
            <Route
              path="/school/courseSections/:courseSectionId/assignments/:assignmentId/questions"
              element={
                <Suspense fallback={<LoadingLayout showAfterDelay />}>
                  <AssignmentQuestionsView />
                </Suspense>
              }
            />
            <Route
              path="/school/courseSections/:courseSectionId/assignments/:assignmentId/prompts"
              element={
                <Suspense fallback={<LoadingLayout showAfterDelay />}>
                  <AssignmentPromptsView />
                </Suspense>
              }
            />

            <Route
              path="/school/courseSections/:courseSectionId/assignments/:assignmentId"
              element={
                <Suspense fallback={<LoadingLayout showAfterDelay />}>
                  <SchoolAssignmentView />
                </Suspense>
              }
            />
          </Route>
        </Route>

        {/* Layout routes */}
        <Route element={<Layout />}>
          <Route
            path="/jobs/:jobId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <JobView />
              </Suspense>
            }
          />
          <Route
            path="/assignmentGroups/:assignmentGroupId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <AssignmentGroupView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/apiTest"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <ApiTestView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/schools/:schoolId/terms/:termId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <AdministratorSchoolTermView />
              </Suspense>
            }
          />
          <Route
            path="/connections/:connectionId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <ConnectionsView />
              </Suspense>
            }
          />
          <Route
            path="/ltiJobSchedules/:scheduleId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <LtiJobScheduleView />
              </Suspense>
            }
          />
          <Route
            path="/jobSchedules/:jobScheduleId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <JobScheduleView />
              </Suspense>
            }
          />
          <Route
            path="/schools"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CompaniesView type={CompanyType.SCHOOL} />
              </Suspense>
            }
          />
          <Route
            path="/distributors"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CompaniesView type={CompanyType.DISTRIBUTOR} />
              </Suspense>
            }
          />
          <Route
            path="/publishers"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CompaniesView type={CompanyType.PUBLISHER} />
              </Suspense>
            }
          />
          <Route
            path="/school/questions/:questionId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <QuestionReportCard />
              </Suspense>
            }
          />
          <Route
            path="/transactions"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <TransactionView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/videoQueue"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <VideoQueueView />
              </Suspense>
            }
          />
          <Route
            path="/companies"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CompaniesView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/courseware/build"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PublisherBuildCoursewareView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/courseware/price"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PublisherPriceCoursewareView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/courseware/adopt"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PublisherAdoptCoursewareView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/courseware/launch"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PublisherLaunchCoursewareView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/courseware/manage"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PublisherManageCoursewareView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/courseware"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PublisherCoursewareView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/books/:bookId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PublisherBookView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/invoices"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PublisherInvoicesView />
              </Suspense>
            }
          />
          <Route
            path="/accessCodes"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <AccessCodesView />
              </Suspense>
            }
          />
          <Route
            path="/gcloud"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <GcloudView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/connectionRequests"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <ConnectionRequestView />
              </Suspense>
            }
          />
          <Route
            path="/gcloud/callback"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <GcloudCallback />
              </Suspense>
            }
          />
          <Route
            path="/canvas"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CanvasView />
              </Suspense>
            }
          />
          <Route
            path="/accounting"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <AccountingView />
              </Suspense>
            }
          />
          <Route
            path="/school/products/:productId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <SchoolProductView />
              </Suspense>
            }
          />
          <Route
            path="/school/questions/:questionId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <SchoolQuestionView />
              </Suspense>
            }
          />
          <Route
            path="/accessCodes/new"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CreateBulkAccessCodeView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/payments/new"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CreatePublisherPaymentView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/invoices/new"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CreatePublisherInvoiceView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/invoices/:invoiceId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PublisherInvoiceView />
              </Suspense>
            }
          />
          <Route
            path="/billingPeriods"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <BillingPeriodsView />
              </Suspense>
            }
          />
          <Route
            path="/billingPeriods/:billingPeriodId/accountingStatements"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <AccountingStatementsView />
              </Suspense>
            }
          />
          <Route
            path="/billingPeriods/:billingPeriodId/accountingStatements/:accountingStatementId/charges"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <BillingChargesView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/tasks"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <TaskView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/monday"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <MondayView />
              </Suspense>
            }
          />
          <Route
            path="/billingList"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <BillingListView />
              </Suspense>
            }
          />
          <Route
            path="/billingPeriods/:billingPeriodId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <BillingPeriodView />
              </Suspense>
            }
          />
          <Route
            path="/transactions/:transactionId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <BillingTransactionView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/courses/:courseSectionId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PublisherCourseView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/courseRequests/new"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PublisherNewCourseRequestView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/schools/:schoolId/terms"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <AdministratorTermsView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/distributors"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <DistributorsListView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/publishers"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PublishersListView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/schools"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <SchoolsListView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/distributors/onboard"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <AddDistributorView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/publishers/:publisherId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <AdministratorPublisherView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/publishers/onboard"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <AddPublisherView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/publishers/:publisherId/fees"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <AdministratorPublisherFeesView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/distributors/:distributorId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <AdministratorDistributorView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/schools/:schoolId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <AdministratorSchoolView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/courses"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <AdministratorCoursesView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/schools/:schoolId/connections"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <AdministratorConnectionsView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/schools/:schoolId/connections/:connectionId/ltiJobSchedules"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <AdministratorConnectionLtiJobSchedulesView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/schools/:schoolId/connections/:connectionId/ltiJobs/:ltiJobId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <AdministratorConnectionLtiJobView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/schools/:schoolId/connections/:connectionId/ltiJobs"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <AdministratorConnectionLtiJobsView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/schools/:schoolId/connections/:connectionId/apiJobSchedules"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <AdministratorConnectionApiJobSchedulesView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/schools/:schoolId/connections/:connectionId/apiJobs"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <AdministratorConnectionApiJobsView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/schools/:schoolId/connections/:connectionId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <AdministratorConnectionView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/schools/onboard"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <AdministratorCreateSchoolView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/courseware/onboard"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <AdministratorCreateCoursewareView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/prices/onboard"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <AdministratorCreatePriceView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/courses/new"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <AdministratorNewCourseView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/courseRequests/:requestId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <AdministratorCourseRequestView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/courseRequests/:requestId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PublisherCourseRequestView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/courses"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PublisherCoursesView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/dashboards"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <AdministratorDashboardsView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/catalog"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <AdministratorCatalogView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/adoptions"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <AdministratorAdoptionsView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/adoptions/email"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <AdministratorAdoptionEmailsView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/adoptions/onboard"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <AdministratorCreateAdoptionView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/adoptions/onboard"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PublisherCreateAdoptionView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/catalog/:editionId/imageAccessibilityJobs"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CatalogImageAccessibilityJobsView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/catalog/:editionId/imageAccessibilityJobs/:jobId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CatalogImageAccessibilityJobView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/catalog/:editionId/conversionJobs"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CatalogEditionConversionJobsView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/catalog/:editionId/conversionJobs/:jobId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CatalogEditionConversionJobView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/catalog/:editionId/assessmentJobs"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CatalogEditionAssessmentJobsView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/catalog/:editionId/assessmentJobs/:jobId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CatalogEditionAssessmentJobView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/onixJobs/:jobId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <OnixJobView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/onixJobs"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <OnixJobsView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/identifiers"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <AdministratorIdentifiersView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/catalog/:editionId/tableOfContents"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CatalogEditionTableOfContentsView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/catalog/:editionId/supplementals"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CatalogEditionSupplementalsView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/catalog/:editionId/supplementals"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CatalogEditionSupplementalsView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/demos"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <ProductDemosView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/catalog/:editionId/maths"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <AdministratorMathsView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/demos"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PublisherDemosView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/adoptions"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PublisherAdoptionsView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/titleRequests"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PublisherTitleRequestsView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/catalog/:catalogRequestId/approve"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <AdministratorApproveCatalogRequestView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/catalog/:editionId/courseware"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CatalogCreateCoursewareView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/catalog/requests/:requestId/provision"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CatalogProvisionRequestView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/courses/:courseSectionId/clone/:cloneId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CloneJobView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/courses/:courseSectionId/clone"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CoursesCloneCourseSectionView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/courses/:courseSectionId/provision"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CoursesProvisionCourseSectionView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/catalog/:editionId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CatalogEditionView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/catalog/:editionId/learningObjectives"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CatalogLearningObjectivesView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/catalog/:editionId/slides"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CatalogEditionSlidesView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/catalog/:editionId/slides/generate"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CatalogEditionGenerateSlidesView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/catalog/:editionId/flashcards/generate"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CatalogEditionGenerateFlashcardsView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/catalog/:editionId/flashcards/generate"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CatalogEditionGenerateFlashcardsView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/catalog/:editionId/slides/upload"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CatalogEditionUploadSlidesView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/catalog/:editionId/questions"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CatalogEditionQuestionsView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/catalog/:editionId/prompts"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CatalogEditionPromptsView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/catalog/:editionId/flashcards"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CatalogEditionFlashcardsView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/catalog/:editionId/flashcards/:flashcardId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CatalogEditionEditFlashcardView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/catalog/:editionId/flashcards/:flashcardId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CatalogEditionEditFlashcardView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/catalog/:editionId/questions/:questionId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CatalogEditionEditQuestionView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/catalog/:editionId/questions/:questionId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CatalogEditionEditQuestionView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/catalog/:editionId/questions/new"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CatalogEditionNewQuestionView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/catalog/:editionId/questions/generate"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CatalogEditionGenerateQuestionsView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/catalog/:editionId/questions/generate"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CatalogEditionGenerateQuestionsView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/catalog/:editionId/questions/new"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CatalogEditionNewQuestionView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/catalog/:editionId/flashcards/new"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CatalogEditionNewFlashcardView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/catalog/:editionId/flashcards/new"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CatalogEditionNewFlashcardView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/catalog/:editionId/questions"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CatalogEditionQuestionsView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/catalog/:editionId/prompts"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CatalogEditionPromptsView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/catalog/:editionId/flashcards"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CatalogEditionFlashcardsView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/catalog/new"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CatalogNewEditionView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/catalog"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PublisherCatalogView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/catalog/:editionId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CatalogEditionView />
              </Suspense>
            }
          />
          <Route
            path="/administrator/catalogRequests/new"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <AdministratorNewCatalogRequestView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/catalogRequests/new"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <NewCatalogRequestView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/catalog/new"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CatalogNewEditionView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/transactions"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PublisherTransactionsView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/transactions/:transactionId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PublisherTransactionView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/assignments/new"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CreatePublisherAssignmentView />
              </Suspense>
            }
          />
          <Route
            path="/school/questions/new"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <SchoolCreateQuestionView />
              </Suspense>
            }
          />
          <Route
            path="/school/questions/ai"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <SchoolAIQuestionView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/assignments/:assignmentId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PublisherAssignmentView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/enrollments/new"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CreatePublisherEnrollmentView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/enrollments/:enrollmentId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PublisherEnrollmentView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/editions/new"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CreatePublisherEditionView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/editions/:editionId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PublisherEditionView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/questions/new"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CreatePublisherQuestionView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/questions/:questionId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PublisherQuestionView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/launches/new"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CreatePublisherLaunchView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/launches/:productId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PublisherLaunchView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/editions/:editionId/clone"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PublisherCloneEditionView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/products/new"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CreatePublisherProductView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/courseware/new"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CreatePublisherCoursewareView />
              </Suspense>
            }
          />
          <Route
            path="/privacyPolicy"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PrivacyPolicyView />
              </Suspense>
            }
          />
          <Route
            path="/termsOfUse"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <TermsOfUseView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/products/:productId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PublisherProductView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/prices/:priceId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PublisherPriceView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/books/new"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CreatePublisherBookView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/adoptions/clone"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <ClonePublisherAdoptionView />
              </Suspense>
            }
          />
          <Route
            path="/adoptions/new"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CreateAdoptionView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/adoptions/new"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CreatePublisherAdoptionView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/adoptions/:productId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PublisherAdoptionView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/courseSections/:courseSectionId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PublisherCourseSectionView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/terms/:termId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PublisherTermView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/prices/new"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CreatePublisherPriceView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/prices/:priceId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PublisherPriceView />
              </Suspense>
            }
          />
          <Route
            path="/courseware"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PricesView />
              </Suspense>
            }
          />
          <Route
            path="/companies/:companyId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CompanyView />
              </Suspense>
            }
          />
          <Route
            path="/products/new"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CreateProductView />
              </Suspense>
            }
          />
          <Route
            path="/companyProfile/:companyId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CompanyProfileView />
              </Suspense>
            }
          />
          <Route
            path="/myProfile"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PersonProfileView />
              </Suspense>
            }
          />
          <Route
            path="/products/:productId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <ProductView />
              </Suspense>
            }
          />
          <Route
            path="/connections/new"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CreateConnectionView />
              </Suspense>
            }
          />
          <Route
            path="/contents/new"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CreateContentView />
              </Suspense>
            }
          />
          <Route
            path="/notes/:noteId/contents/:contentId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <NoteContentView />
              </Suspense>
            }
          />
          <Route
            path="/contents/:contentId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <ContentView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/contents/new"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CreatePublisherContentView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/contents/:contentId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PublisherContentView />
              </Suspense>
            }
          />
          <Route
            path="/jobSchedules/new"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CreateJobScheduleView />
              </Suspense>
            }
          />
          <Route
            path="/jobs/new"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CreateJobView />
              </Suspense>
            }
          />
          <Route
            path="/prices/assign/:priceId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PriceAssignView />
              </Suspense>
            }
          />
          <Route
            path="/costs/new"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CreateCostView />
              </Suspense>
            }
          />
          <Route
            path="/messages"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <MessagesView />
              </Suspense>
            }
          />
          <Route
            path="/publisher/questions/upload"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <UploadPublisherQuestionsView />
              </Suspense>
            }
          />
          <Route
            path="/questions/new"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CreateQuestionView />
              </Suspense>
            }
          />
          <Route
            path="/questions/:questionId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <QuestionView />
              </Suspense>
            }
          />
          <Route
            path="/prices/new"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CreatePriceView />
              </Suspense>
            }
          />
          <Route
            path="/costs/:costId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CostView />
              </Suspense>
            }
          />
          <Route
            path="/prices/:priceId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PriceView />
              </Suspense>
            }
          />
          <Route
            path="/books/:bookId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <BookView />
              </Suspense>
            }
          />
          <Route
            path="/people/new"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CreatePersonView />
              </Suspense>
            }
          />
          <Route
            path="/people/:personId/schoolAssignments"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PersonSchoolAssignmentsView />
              </Suspense>
            }
          />
          <Route
            path="/editions/new"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CreateEditionView />
              </Suspense>
            }
          />
          <Route
            path="/editions/:editionId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <EditionView />
              </Suspense>
            }
          />
          <Route
            path="/enrollments/new"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CreateEnrollmentView />
              </Suspense>
            }
          />
          <Route
            path="/terms/new"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CreateTermView />
              </Suspense>
            }
          />
          <Route
            path="/courseSections/new"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CreateCourseSectionView />
              </Suspense>
            }
          />
          <Route
            path="/companies/new"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CreateCompanyView />
              </Suspense>
            }
          />
          <Route
            path="/books/new"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CreateBookView />
              </Suspense>
            }
          />
          <Route
            path="/people"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PeopleView />
              </Suspense>
            }
          />
          <Route
            path="/assignments/:assignmentId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <AssignmentView />
              </Suspense>
            }
          />
          <Route
            path="/school/modules/:moduleId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <ModuleView />
              </Suspense>
            }
          />
          <Route
            path="/people/:personId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PersonView />
              </Suspense>
            }
          />
          <Route
            path="/companies/:companyId/people/:personId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PersonView />
              </Suspense>
            }
          />
          <Route
            path="/companies/:companyId/people"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PeopleView />
              </Suspense>
            }
          />
          <Route
            path="/enrollments/:enrollmentId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <EnrollmentView />
              </Suspense>
            }
          />
          <Route
            path="/access/:accessId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <BookView />
              </Suspense>
            }
          />
          <Route
            path="/courseSections/:courseSectionId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <CourseSectionView />
              </Suspense>
            }
          />
          <Route
            path="/terms/:termId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <TermView />
              </Suspense>
            }
          />
          <Route
            index
            path="/"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <MainView />
              </Suspense>
            }
          />
          <Route
            path="*"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <NoMatchView />
              </Suspense>
            }
          />
        </Route>

        {/* Non-layout routes */}
        <Route>
          <Route
            path="/school/attendance/:attendanceId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <AttendanceView />
              </Suspense>
            }
          />
          <Route
            path="/school/attendance/:attendanceId/question"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <AttendanceQuestionView />
              </Suspense>
            }
          />
          <Route
            path="/school/polls/:assignmentId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PollView />
              </Suspense>
            }
          />
          <Route
            path="/school/polls/:assignmentId/question"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <PollQuestionView />
              </Suspense>
            }
          />
          <Route
            path="/products/content/:productId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <ContentProductView />
              </Suspense>
            }
          />
          <Route
            path="/school/assignments/:assignmentId/attempt"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <AssignmentAttemptView />
              </Suspense>
            }
          />
          <Route
            path="/contents/document/:contentId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <DocumentContentView />
              </Suspense>
            }
          />
          <Route
            path="/editions/content/:editionId"
            element={
              <Suspense fallback={<LoadingLayout showAfterDelay />}>
                <ContentEditionView />
              </Suspense>
            }
          />
        </Route>
      </Route>
    </Routes>
  );
};

export default RouteList;
